import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Loading from '../loading/Loading'
import EditProposalModal from '../modals/EditProposalModal'

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Pagination from '@mui/material/Pagination'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import FormControl from '@mui/material/FormControl'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import Collapse from '@mui/material/Collapse'
import Tooltip from '@mui/material/Tooltip'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import BlockIcon from '@mui/icons-material/Block'

import { AdminManage, ReconcileStyle } from '../styles'

import { PAGES } from '../navigation/routes'

import {
    fetchFindStatus,
    fetchRemediateStatus,
    fetchSources,
    startFind,
    startRemediate,
    remediateSingleViolation,
    fetchViolations,
    ignoreViolation,
    fetchProposals,
    approveProposal,
    rejectProposal,
    dismissProposal,
    fetchSettings,
    saveSettings,
} from '../../reducers/automationReducer'
import { LIST_FILTER_KEYS } from '../../services/automation'
import { safeHref } from '../../utils/safeUrl'
import { summarizeRunStats } from './runStatsSummary'
import { computeEtaMs, formatDuration, progressLabel, progressPercent } from './runProgress'
import SourceSelect from './SourceSelect'
import ModelSelect from './ModelSelect'
import ConstraintSettings from './ConstraintSettings'

// The status endpoint is a cheap single-row read, so it polls fast to keep the
// progress bar, working-on line, and completion transition close to live. The list
// refetches are the expensive requests (paginated polymorphic serialization), so
// they ride along only every LIST_REFRESH_TICKS-th status tick — the finishing tick
// always refreshes regardless.
const POLL_INTERVAL_MS = 1000
const LIST_REFRESH_TICKS = 10

const KIND_COLOR = { add: 'success', remove: 'error', replace: 'info' }
const STATE_COLOR = { idle: 'default', running: 'info', completed: 'success', failed: 'error' }

const FILTER_DEFAULTS = Object.fromEntries(LIST_FILTER_KEYS.map(key => [key, '']))

const CONSTRAINT_OPTIONS = [
    ['', 'All constraints'], ['expiry', 'Expiry'], ['citation', 'Citations'],
    ['cap', 'Caps'], ['coverage', 'Coverage'], ['link_check', 'Broken links'],
    ['retraction', 'Retractions'],
]
const KIND_OPTIONS = [
    ['', 'All kinds'], ['remove', 'Remove'], ['replace', 'Replace'], ['add', 'Add'],
]
const RESOURCE_TYPE_OPTIONS = [
    ['', 'All types'], ['Book', 'Book'], ['Paper', 'Paper'], ['Video', 'Video'],
    ['Course', 'Course'], ['Podcast', 'Podcast'], ['CodeNotebook', 'Code Notebook'],
    ['Policy', 'Policy'],
]

// Compact filter row for a paginated list: three selects + a debounced search box.
// Debounce lives here so parent state (and the refetch effect keyed on it) only
// changes ~400ms after typing pauses.
const ListFilters = ({ filters, onChange, idPrefix, searchLabel }) => {
    const [searchDraft, setSearchDraft] = useState(filters.search)
    useEffect(() => {
        const handle = setTimeout(() => {
            if (searchDraft !== filters.search) {
                onChange({ ...filters, search: searchDraft })
            }
        }, 400)
        return () => clearTimeout(handle)
    }, [searchDraft, filters, onChange])

    const select = (key, label, options) => (
        <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel id={`${idPrefix}-${key}-label`}>{label}</InputLabel>
            <Select
                labelId={`${idPrefix}-${key}-label`}
                label={label}
                value={filters[key]}
                onChange={e => onChange({ ...filters, [key]: e.target.value })}
            >
                {options.map(([value, text]) => (
                    <MenuItem key={value} value={value}>{text}</MenuItem>
                ))}
            </Select>
        </FormControl>
    )

    return (
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mb: 2 }}>
            {select('constraint', 'Constraint', CONSTRAINT_OPTIONS)}
            {select('kind', 'Kind', KIND_OPTIONS)}
            {select('resourcetype', 'Resource type', RESOURCE_TYPE_OPTIONS)}
            <TextField
                id={`${idPrefix}-search`}
                type='search'
                size='small'
                label={searchLabel}
                value={searchDraft}
                onChange={e => setSearchDraft(e.target.value)}
            />
        </Stack>
    )
}

const formatTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—')

// Any non-empty filter value means the current empty result is due to filtering,
// not an actually-empty queue — the empty-state copy should say so.
const hasActiveFilters = (filters) => Object.values(filters).some(Boolean)

// A proposal's link comes straight from the pipeline; a non-http(s) value is shown
// as inert text so the reviewer can see what was proposed without it being clickable
// (issue #404).
const renderLink = (link) => {
    if (!link) return null
    const href = safeHref(link)
    return href
        ? <a href={href} target='_blank' rel='noreferrer'>link</a>
        : <Typography variant='caption' component='span'>unsafe link (not http/https): {link}</Typography>
}

const renderResource = (resource) => {
    if (!resource) return '—'
    return (
        <Box>
            <Typography variant='body2'><strong>{resource.title}</strong></Typography>
            <Typography variant='caption' component='div'>
                {resource.type}{resource.pub_date ? ` · ${resource.pub_date}` : ''}
            </Typography>
            {renderLink(resource.link)}
        </Box>
    )
}

const renderProposed = (data) => {
    if (!data) return '—'
    const title = data.title || data.term || '(untitled)'
    return (
        <Box>
            <Typography variant='body2'><strong>{title}</strong></Typography>
            {(data.resourcetype || data.pub_date) && (
                <Typography variant='caption' component='div'>
                    {data.resourcetype}{data.pub_date ? ` · ${data.pub_date}` : ''}
                </Typography>
            )}
            {renderLink(data.link)}
        </Box>
    )
}

const renderReasons = (item) => {
    const reasons = Array.isArray(item.reasons) ? item.reasons : []
    if (reasons.length) return reasons.join('; ')
    return item.rationale || '—'
}

// The rationale is the LLM's justification for the fit score, so it lives on the score:
// the dotted underline + info icon signal there's a tooltip to hover.
const renderFitScore = (proposal) => {
    if (proposal.fit_score == null) return '—'
    const score = proposal.fit_score.toFixed(2)
    if (!proposal.rationale) return score
    return (
        <Tooltip title={proposal.rationale} arrow>
            <Box
                component='span'
                sx={{
                    cursor: 'help',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    borderBottom: '1px dotted',
                }}
            >
                {score}
                <InfoOutlinedIcon sx={{ fontSize: '1rem' }} color='action' />
            </Box>
        </Tooltip>
    )
}

// A run's outcome: short labeled summary (nonzero counters only, tooltips explain
// each) with the full raw stats dict behind an "All stats" toggle.
const RunStats = ({ stats }) => {
    const [expanded, setExpanded] = useState(false)
    return (
        <Box sx={{ marginTop: '10px' }}>
            {summarizeRunStats(stats).map(({ key, text, tooltip }) => (
                <Tooltip key={key} title={tooltip} arrow>
                    <Chip sx={ReconcileStyle.statChip} size='small' label={text} />
                </Tooltip>
            ))}
            <Button size='small' sx={{ textTransform: 'none' }} aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>
                All stats
                <ExpandMoreIcon fontSize='small' sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </Button>
            <Collapse in={expanded}>
                <Box sx={{ marginTop: '6px' }}>
                    {Object.entries(stats).map(([key, value]) => (
                        <Chip
                            key={key}
                            sx={ReconcileStyle.statChip}
                            size='small'
                            variant='outlined'
                            label={`${key.replace(/_/g, ' ')}: ${typeof value === 'object' ? JSON.stringify(value) : value}`}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    )
}

// Re-renders once a second while `active`, so elapsed time counts up live between polls.
const useNow = (active) => {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (!active) return undefined
        setNow(Date.now())
        const interval = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(interval)
    }, [active])
    return now
}

const toMs = (iso) => (iso ? new Date(iso).getTime() : null)

// The trigger + progress card shared by both passes. `extra` renders in the header
// row (the remediate card puts its source dropdown there).
const StatusCard = ({ run, running, disabled, onRun, label, extra }) => {
    const now = useNow(running)
    const elapsedMs = running && run.started ? Math.max(0, now - toMs(run.started)) : null
    const tookMs = run.finished && run.started ? toMs(run.finished) - toMs(run.started) : null
    // Only the remediate loop writes progress; find runs render the indeterminate bar.
    const percent = running ? progressPercent(run.progress) : null
    const barLabel = running ? progressLabel(run.progress) : null
    const etaMs = running && percent !== null ? computeEtaMs(run.progress, elapsedMs) : null
    return (
        <Paper sx={ReconcileStyle.statusCard} elevation={2}>
            <Box sx={ReconcileStyle.statusHeader}>
                <Button
                    {...ReconcileStyle.runButton.props}
                    sx={ReconcileStyle.runButton.style}
                    onClick={onRun}
                    disabled={disabled}
                >
                    <PlayArrowIcon />&nbsp;{label}
                </Button>
                {extra}
                <Chip label={`Status: ${run.state}`} color={STATE_COLOR[run.state] || 'default'} />
                {running && <CircularProgress size={22} />}
            </Box>
            {running && (
                <Box sx={{ marginBottom: '10px' }}>
                    {barLabel && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant='body2'><strong>{barLabel}</strong></Typography>
                            <Typography variant='body2'>{`${Math.round(percent)}%`}</Typography>
                        </Box>
                    )}
                    <LinearProgress
                        variant={percent === null ? 'indeterminate' : 'determinate'}
                        value={percent === null ? undefined : percent}
                        sx={{ marginTop: '4px' }}
                    />
                    {run.progress?.current && (
                        <Typography variant='body2' sx={{ fontStyle: 'italic', marginTop: '6px' }}>
                            Working on: {run.progress.current}
                        </Typography>
                    )}
                </Box>
            )}
            {run.state !== 'idle' && (
                <>
                    <Typography {...ReconcileStyle.metaText}>
                        Started: {formatTime(run.started)}
                        {elapsedMs !== null && ` · Elapsed: ${formatDuration(elapsedMs)}`}
                        {running && percent !== null && (
                            etaMs === null
                                ? ' · ETA: estimating…'
                                : ` · ETA: ~${formatDuration(etaMs)} remaining`
                        )}
                    </Typography>
                    {run.finished && (
                        <Typography {...ReconcileStyle.metaText}>
                            Finished: {formatTime(run.finished)}
                            {tookMs !== null && tookMs >= 0 && ` · took ${formatDuration(tookMs)}`}
                        </Typography>
                    )}
                    {run.stats && <RunStats stats={run.stats} />}
                    {run.state === 'failed' && run.error && (
                        <Typography sx={ReconcileStyle.errorText} variant='body2'>Error: {run.error}</Typography>
                    )}
                </>
            )}
        </Paper>
    )
}

const ManageReconciliation = () => {
    const user = useSelector(state => state.user)
    const {
        findRun, remediateRun, findTriggering, remediateTriggering,
        violations, loadingViolations, proposals, loadingProposals, sources, features,
        llmModels, llmDefault, settings,
    } = useSelector(state => state.automation)
    const dispatch = useDispatch()

    const [violationsPage, setViolationsPage] = useState(1)
    const [proposalsPage, setProposalsPage] = useState(1)
    const [activeStep, setActiveStep] = useState(0)
    const [violationFilters, setViolationFilters] = useState(FILTER_DEFAULTS)
    const [proposalFilters, setProposalFilters] = useState(FILTER_DEFAULTS)

    // Per-run source selection: null until the list arrives, then all keys checked.
    const [selectedSources, setSelectedSources] = useState(null)

    // Per-run model choice: null until the bootstrap payload names the server's default.
    // Stays null for an uncurated env config — the trigger then omits the param and the
    // server default applies.
    const [selectedModel, setSelectedModel] = useState(null)

    const findRunning = findRun.state === 'running'
    const remediateRunning = remediateRun.state === 'running'
    // The backend serializes the two passes, so a run in either phase blocks starting the other.
    const anyRunning = findRunning || remediateRunning

    // Initial status read for both phases. RequireStaff (App.js) guarantees
    // this component only mounts for a loaded, staff user — this check just
    // guards against `user` being momentarily stale on the very first
    // effect run.
    useEffect(() => {
        if (user?.staff) {
            dispatch(fetchFindStatus())
            dispatch(fetchRemediateStatus())
        }
    }, [user, dispatch])

    // (Re)load each queue when its page or filters change.
    useEffect(() => {
        if (user?.staff) dispatch(fetchViolations(violationsPage, violationFilters))
    }, [user, violationsPage, violationFilters, dispatch])

    useEffect(() => {
        if (user?.staff) dispatch(fetchProposals(proposalsPage, proposalFilters))
    }, [user, proposalsPage, proposalFilters, dispatch])

    useEffect(() => {
        if (user?.staff) dispatch(fetchSources())
    }, [user, dispatch])

    useEffect(() => {
        if (user?.staff) dispatch(fetchSettings())
    }, [user, dispatch])

    useEffect(() => {
        if (selectedSources === null && sources.length > 0) {
            setSelectedSources(sources.map(source => source.key))
        }
    }, [sources, selectedSources])

    useEffect(() => {
        if (selectedModel === null && llmDefault) {
            setSelectedModel(llmDefault)
        }
    }, [llmDefault, selectedModel])

    // Poll the find pass while it runs, refreshing the violations queue with each status
    // read — violations are persisted as the pass works, so the queue fills in live.
    useEffect(() => {
        if (!findRunning) return
        let cancelled = false
        let tick = 0
        const poll = async () => {
            const listTick = tick % LIST_REFRESH_TICKS === 0
            tick += 1
            const latest = await dispatch(fetchFindStatus())
            const finished = latest && latest.state !== 'running'
            // The finishing tick cancels this effect itself (running flips false), so it
            // must refresh anyway; interim ticks respect cancellation and the list cadence.
            if (!finished && (cancelled || !listTick)) return
            dispatch(fetchViolations(violationsPage, violationFilters))
        }
        poll()
        const interval = setInterval(poll, POLL_INTERVAL_MS)
        return () => { cancelled = true; clearInterval(interval) }
    }, [findRunning, dispatch, violationsPage, violationFilters])

    // Poll the remediate pass while it runs, refreshing both queues with each status read —
    // proposals are persisted one violation at a time (and remediated violations leave the
    // pending queue), so both tables update live during the run.
    useEffect(() => {
        if (!remediateRunning) return
        let cancelled = false
        let tick = 0
        const poll = async () => {
            const listTick = tick % LIST_REFRESH_TICKS === 0
            tick += 1
            const latest = await dispatch(fetchRemediateStatus())
            const finished = latest && latest.state !== 'running'
            // The finishing tick cancels this effect itself (running flips false), so it
            // must refresh anyway; interim ticks respect cancellation and the list cadence.
            if (!finished && (cancelled || !listTick)) return
            dispatch(fetchProposals(proposalsPage, proposalFilters))
            dispatch(fetchViolations(violationsPage, violationFilters))
        }
        poll()
        const interval = setInterval(poll, POLL_INTERVAL_MS)
        return () => { cancelled = true; clearInterval(interval) }
    }, [remediateRunning, dispatch, proposalsPage, violationsPage, proposalFilters, violationFilters])

    const selected = selectedSources || []
    const allSelected = sources.length > 0 && selected.length === sources.length
    // Gated on selectedSources !== null so the one render frame between the source list
    // arriving and the init effect populating the selection doesn't flash the "select at
    // least one" warning / disabled button before the all-checked default applies.
    const noneSelected = selectedSources !== null && sources.length > 0 && selected.length === 0

    const handleViolationFilters = (next) => {
        setViolationFilters(next)
        setViolationsPage(1)
    }
    const handleProposalFilters = (next) => {
        setProposalFilters(next)
        setProposalsPage(1)
    }

    const handleSaveSettings = (draft) => dispatch(saveSettings(draft))

    const handleFind = () => dispatch(startFind())
    // All-checked sends no filter (server default), so adding a source later doesn't
    // silently exclude it for users who never touched the checkboxes. A genuinely-zero
    // selection already disables the Remediate button via noneSelected, so
    // selected.length === 0 here only ever means "the source list never loaded" —
    // fall back to the server default (all sources) rather than posting an empty list.
    const handleRemediate = () => dispatch(startRemediate(
        allSelected || selected.length === 0 ? null : selected, selectedModel,
    ))

    const actOnProposal = (thunk) => async (id) => {
        const ok = await dispatch(thunk(id))
        if (ok) dispatch(fetchProposals(proposalsPage, proposalFilters))
    }

    const handleApprove = actOnProposal(approveProposal)
    const handleReject = actOnProposal(rejectProposal)
    const handleDismiss = actOnProposal(dismissProposal)

    const handleIgnore = async (id) => {
        const ok = await dispatch(ignoreViolation(id))
        if (ok) dispatch(fetchViolations(violationsPage, violationFilters))
    }

    // Targeted run (feature-flagged): remediate just this violation, honoring the
    // Step 2 source selection with the same all-checked → server-default logic as
    // handleRemediate — so "this violation × only this source" is testable.
    const handleRemediateOne = (id) => {
        dispatch(remediateSingleViolation(
            id, allSelected || selected.length === 0 ? null : selected, selectedModel,
        ))
        // The run's outcome (status card, stats, error, incoming proposal) all live on
        // Step 3 — jump there so the result of the click is actually visible.
        setActiveStep(2)
    }

    return (
        <Container sx={AdminManage.container}>
            <Typography {...AdminManage.title} gutterBottom>
                {PAGES.manage_automation.title}
            </Typography>
            <Typography variant='p' gutterBottom>
                Configure which constraints run and their thresholds on the <strong>Configuration</strong> tab.
                Reconciliation itself runs in two steps: first <strong>find violations</strong> — a
                scan for resources that fall short of the hub's rules (e.g. expired); usually
                fast, though refreshing citation data can take a minute. Then
                <strong> remediate</strong> — for each violation, query external sources and propose
                a concrete change to review. Both run in the background; remediation may take several
                minutes. In practice only the <strong>paper</strong> and <strong>book</strong> sources
                reliably return usable candidates; the rest are marked Experimental in the source picker
                and stay selectable for exploration.
            </Typography>

            <Tabs
                value={activeStep}
                onChange={(event, value) => setActiveStep(value)}
                sx={{ mb: 2 }}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab label='Step 1 · Configuration' />
                <Tab label='Step 2 · Find violations' />
                <Tab label='Step 3 · Remediate' />
            </Tabs>

            {/* Step 1 — Configuration */}
            {activeStep === 0 && (
                <ConstraintSettings settings={settings} onSave={handleSaveSettings} />
            )}

            {/* Step 2 — Find violations */}
            {activeStep === 1 && (
            <>
            <StatusCard
                run={findRun}
                running={findRunning}
                disabled={anyRunning || findTriggering}
                onRun={handleFind}
                label='Find violations'
            />

            <Typography variant='h6' gutterBottom>
                Pending Violations ({violations.count})
            </Typography>

            <ListFilters filters={violationFilters} onChange={handleViolationFilters} idPrefix='violations' searchLabel='Search violations' />

            {loadingViolations && violations.results.length === 0 ? (
                <Container {...AdminManage.loading.props} sx={AdminManage.loading.style}>
                    <Loading />
                </Container>
            ) : violations.results.length === 0 ? (
                <Typography sx={ReconcileStyle.emptyText} variant='h5'>
                    {hasActiveFilters(violationFilters)
                        ? 'No violations match the current filters.'
                        : 'No pending violations.'}
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table sx={AdminManage.table} aria-label="violations">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Kind</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Constraint</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Resource</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Reasons</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {violations.results.map(violation => (
                                    <TableRow key={`${violation.id}`} sx={AdminManage.tableRow}>
                                        <TableCell>
                                            <Chip label={violation.proposed_kind} color={KIND_COLOR[violation.proposed_kind] || 'default'} size='small' />
                                        </TableCell>
                                        <TableCell {...AdminManage.typeAlign}>{violation.constraint_key_pretty}</TableCell>
                                        <TableCell {...AdminManage.typeAlign}>{renderResource(violation.subject)}</TableCell>
                                        <TableCell {...AdminManage.typeAlign}>{renderReasons(violation)}</TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Box sx={ReconcileStyle.actionCell}>
                                                {/* Server-flagged targeted run; the normal pass's guards
                                                    (dedup, re-validation, holds) still apply. */}
                                                {features.per_violation_remediation && (
                                                    <Button {...ReconcileStyle.runButton.props} sx={ReconcileStyle.runButton.style} size='small' disabled={anyRunning || remediateTriggering || noneSelected} onClick={() => handleRemediateOne(violation.id)}>
                                                        <PlayArrowIcon fontSize='small' />&nbsp;Remediate
                                                    </Button>
                                                )}
                                                {/* Pre-proposal veto: the violation stops being re-detected, and
                                                    remediation never spends source/LLM calls on it. */}
                                                <Button {...ReconcileStyle.dismiss.props} sx={ReconcileStyle.dismiss.style} size='small' onClick={() => handleIgnore(violation.id)}>
                                                    <BlockIcon fontSize='small' />&nbsp;Ignore
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        sx={AdminManage.pagination}
                        count={violations.total_pages}
                        page={violationsPage}
                        onChange={(event, value) => setViolationsPage(value)}
                    />
                </>
            )}

            </>
            )}

            {/* Step 3 — Remediate */}
            {activeStep === 2 && (
            <>
            <StatusCard
                run={remediateRun}
                running={remediateRunning}
                disabled={anyRunning || remediateTriggering || noneSelected}
                onRun={handleRemediate}
                label='Remediate'
                extra={selectedSources !== null && sources.length > 0 && (
                    <Box>
                        {/* Wait for the init effect to populate the selection before painting —
                            otherwise the count flashes "0 of N" for the one render frame before
                            the all-checked default applies. */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <SourceSelect
                                sources={sources}
                                selected={selected}
                                onChange={setSelectedSources}
                                disabled={anyRunning || remediateTriggering}
                            />
                            {llmModels.length > 0 && (
                                <ModelSelect
                                    models={llmModels}
                                    value={selectedModel}
                                    onChange={setSelectedModel}
                                    disabled={anyRunning || remediateTriggering}
                                />
                            )}
                        </Box>
                        {noneSelected && (
                            <Typography variant='caption' color='error' component='div'>
                                Select at least one source to remediate.
                            </Typography>
                        )}
                    </Box>
                )}
            />

            <Typography variant='h6' gutterBottom>
                Pending Proposals ({proposals.count})
            </Typography>

            <ListFilters filters={proposalFilters} onChange={handleProposalFilters} idPrefix='proposals' searchLabel='Search proposals' />

            {loadingProposals && proposals.results.length === 0 ? (
                <Container {...AdminManage.loading.props} sx={AdminManage.loading.style}>
                    <Loading />
                </Container>
            ) : proposals.results.length === 0 ? (
                <Typography sx={ReconcileStyle.emptyText} variant='h5'>
                    {hasActiveFilters(proposalFilters)
                        ? 'No proposals match the current filters.'
                        : 'No pending proposals.'}
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table sx={AdminManage.table} aria-label="change proposals">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Kind</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Fit</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Current</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Proposed</TableCell>
                                    <TableCell {...AdminManage.typeAlign}>Reasons</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {proposals.results.map(proposal => (
                                    <TableRow key={`${proposal.id}`} sx={AdminManage.tableRow}>
                                        <TableCell>
                                            <Chip label={proposal.kind} color={KIND_COLOR[proposal.kind] || 'default'} size='small' />
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            {renderFitScore(proposal)}
                                        </TableCell>
                                        <TableCell {...AdminManage.typeAlign}>{renderResource(proposal.old_resource)}</TableCell>
                                        <TableCell {...AdminManage.typeAlign}>{renderProposed(proposal.proposed_data)}</TableCell>
                                        <TableCell {...AdminManage.typeAlign}>{renderReasons(proposal)}</TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Box sx={ReconcileStyle.actionCell}>
                                                {/* Approving a candidate goes through the Review dialog, so the admin
                                                    always looks the proposed changes over first. A REMOVE has no
                                                    candidate to review (plain Approve), and reject would just
                                                    re-propose the same takedown every run — the only veto is Dismiss. */}
                                                {proposal.kind !== 'remove' ? (
                                                    <>
                                                        <EditProposalModal
                                                            proposal={proposal}
                                                            onSaved={() => dispatch(fetchProposals(proposalsPage, proposalFilters))}
                                                            onApprove={() => handleApprove(proposal.id)}
                                                        />
                                                        <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} size='small' onClick={() => handleReject(proposal.id)}>
                                                            <CloseIcon fontSize='small' />&nbsp;Reject
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button {...AdminManage.approve.props} sx={AdminManage.approve.style} size='small' onClick={() => handleApprove(proposal.id)}>
                                                        <CheckIcon fontSize='small' />&nbsp;Approve
                                                    </Button>
                                                )}
                                                <Button {...ReconcileStyle.dismiss.props} sx={ReconcileStyle.dismiss.style} size='small' onClick={() => handleDismiss(proposal.id)}>
                                                    <BlockIcon fontSize='small' />&nbsp;Dismiss
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        sx={AdminManage.pagination}
                        count={proposals.total_pages}
                        page={proposalsPage}
                        onChange={(event, value) => setProposalsPage(value)}
                    />
                </>
            )}
            </>
            )}
        </Container>
    )
}

export default ManageReconciliation
